import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Alert,
  CircularProgress,
  Button,
  Chip,
  Tooltip
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Info as DetailsIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import MetricsWidget from './MetricsWidget';
import SyncRunStatusGrid from './SyncRunStatusGrid';
import HubFlowSummaryPanel from './HubFlowSummaryPanel';
import ServiceDetailsModal from './ServiceDetailsModal';
import PostgresConnectionsModal from './PostgresConnectionsModal';
import { useDashboardMetrics } from '../hooks/useDashboardMetrics';

// API base URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const isCdxUser = () => {
  try {
    const cdxUser = localStorage.getItem('cdx_user');
    return cdxUser === 'true' || cdxUser === true;
  } catch (e) {
    return false;
  }
};

const MetricsDashboard = ({ tenantId = null }) => {
  const [autoRefresh, setAutoRefresh] = useState(true);

  const {
    metrics,
    loading,
    error,
    lastRefresh,
    refresh
  } = useDashboardMetrics(tenantId, autoRefresh, 30000);

  // Scheduler control state
  const [schedulerStatus, setSchedulerStatus] = useState('UNKNOWN');
  const [isLoading, setIsLoading] = useState(false);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showPostgresConnections, setShowPostgresConnections] = useState(false);
  const [postgresConnectionsLoading, setPostgresConnectionsLoading] = useState(false);
  const [postgresConnectionsData, setPostgresConnectionsData] = useState(null);
  const [postgresConnectionsError, setPostgresConnectionsError] = useState(null);
  const [postgresConnectionsScope, setPostgresConnectionsScope] = useState('application');
  const cdxUser = isCdxUser();
  
  // Performance metrics state
  const [performanceMetrics, setPerformanceMetrics] = useState({
    longestRunningTaskDuration: 0,
    averageExecutionTimeFormatted: 'No data',
    nextScheduledCount: 0
  });


  const handleRefresh = () => {
    refresh();
    fetchPerformanceMetrics();
  };

  // Fetch performance metrics from service details
  const fetchPerformanceMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}synctrack/service/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.statistics) {
          setPerformanceMetrics({
            longestRunningTaskDuration: data.statistics.longestRunningTaskDuration || 0,
            averageExecutionTimeFormatted: data.statistics.averageExecutionTimeFormatted || 'No data',
            nextScheduledCount: data.statistics.nextScheduledCount || 0
          });
        }
      }
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    }
  };

  // Fetch performance metrics on component mount
  useEffect(() => {
    fetchPerformanceMetrics();
    fetchSchedulerStatus(); // Initial scheduler status fetch
    
    // Set up periodic scheduler status refresh every 30 seconds
    const statusInterval = setInterval(() => {
      fetchSchedulerStatus();
    }, 30000); // 30 seconds
    
    // Cleanup interval on component unmount
    return () => clearInterval(statusInterval);
  }, []);

  // Scheduler control handlers - unified endpoint
  const handleSchedulerControl = async (process) => {
    setIsLoading(true);
    try {
      // Get tenantId from props or localStorage
      const currentTenantId = tenantId || localStorage.getItem('tenantId');
      
      if (!currentTenantId) {
        toast.error('Tenant ID is required to control the scheduler');
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}scheduler/control`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantId: Number(currentTenantId),
          process: process // 'start' or 'stop'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle the response message from backend
        if (data.message) {
          // Show toast notification based on the message
          if (data.message.includes('already running')) {
            // Scheduler was already running - show info message
            toast.info('Scheduler was already running');
          } else if (data.message.includes('already stopped')) {
            // Scheduler was already stopped - show info message
            toast.info('Scheduler was already stopped');
          } else if (data.message.includes('started successfully')) {
            // Scheduler was successfully started
            toast.success('Scheduler started successfully');
          } else if (data.message.includes('stopped successfully')) {
            // Scheduler was successfully stopped
            toast.success('Scheduler stopped successfully');
          } else {
            // Generic success message
            toast.success(data.message);
          }
        }
        
        // Always update the status from the response
        if (data.status) {
          setSchedulerStatus(data.status);
        } else {
          await fetchSchedulerStatus();
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        toast.error(errorData.message || 'Failed to control scheduler');
      }
    } catch (error) {
      console.error(`Error ${process === 'start' ? 'starting' : 'stopping'} scheduler:`, error);
      toast.error(`Failed to ${process === 'start' ? 'start' : 'stop'} scheduler. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartScheduler = () => {
    handleSchedulerControl('start');
  };

  const handleStopScheduler = () => {
    handleSchedulerControl('stop');
  };

  const fetchSchedulerStatus = async () => {
    try {
      // Get tenantId from props or localStorage
      const currentTenantId = tenantId || localStorage.getItem('tenantId');
      
      // Build URL with tenantId query parameter if available
      let statusUrl = `${API_BASE_URL}scheduler/status`;
      if (currentTenantId) {
        statusUrl += `?tenantId=${currentTenantId}`;
      }
      
      const response = await fetch(statusUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSchedulerStatus(data.status || 'UNKNOWN');
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
      // Don't show toast for status fetch errors as they're automatic and frequent
      // Just log the error silently
    }
  };

  const fetchPostgresConnections = async (scope) => {
    setPostgresConnectionsLoading(true);
    setPostgresConnectionsError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}synctrack/service/postgres/active-connections?state=all&scope=${scope}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message =
          data?.error ||
          data?.message ||
          `Request failed (${response.status} ${response.statusText})`;
        setPostgresConnectionsError(message);
        setPostgresConnectionsData(null);
        return;
      }

      setPostgresConnectionsData(data);
    } catch (err) {
      console.error('Error fetching Postgres connections:', err);
      setPostgresConnectionsError(err.message || 'Failed to fetch Postgres connections');
      setPostgresConnectionsData(null);
    } finally {
      setPostgresConnectionsLoading(false);
    }
  };

  const handleShowPostgresConnections = () => {
    setPostgresConnectionsScope('application');
    setPostgresConnectionsData(null);
    setShowPostgresConnections(true);
    fetchPostgresConnections('application');
  };

  const handlePostgresScopeChange = (newScope) => {
    setPostgresConnectionsScope(newScope);
    fetchPostgresConnections(newScope);
  };

  const handleClosePostgresConnections = () => {
    setShowPostgresConnections(false);
  };

  const handleShowDetails = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}synctrack/service/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServiceDetails(data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Error fetching service details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch scheduler status on component mount
  React.useEffect(() => {
    fetchSchedulerStatus();
  }, []);

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };


  if (loading && !metrics) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          Error loading dashboard metrics: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Controls Header */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,249,250,0.9) 100%)',
          border: '1px solid rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.dark' }}>
              📊 Real-time Metrics
              {tenantId && <Chip label={`Tenant: ${tenantId}`} size="small" color="primary" sx={{ ml: 2 }} />}
              <Chip
                label={`Scheduler: ${schedulerStatus}`}
                size="small"
                color={schedulerStatus === 'RUNNING' ? "success" : "error"}
                sx={{ ml: 1 }}
              />
            </Typography>
            {lastRefresh && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Last updated: {lastRefresh.toLocaleString()}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {cdxUser && (
              <Tooltip title="View active PostgreSQL connections (SyncTrack DB)">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleShowPostgresConnections}
                  startIcon={<StorageIcon />}
                  disabled={postgresConnectionsLoading}
                >
                  Postgres
                </Button>
              </Tooltip>
            )}

            <Tooltip title={autoRefresh ? "Auto-refresh enabled (30s)" : "Auto-refresh disabled"}>
              <Button
                variant={autoRefresh ? "contained" : "outlined"}
                size="small"
                onClick={toggleAutoRefresh}
                startIcon={<AutoAwesomeIcon />}
                color={autoRefresh ? "success" : "primary"}
              >
                Auto
              </Button>
            </Tooltip>
            
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              Refresh
            </Button>

            <Tooltip title={
              isLoading 
                ? "Processing scheduler request..." 
                : schedulerStatus === 'RUNNING' 
                  ? "Click to stop the scheduler (Currently RUNNING)" 
                  : schedulerStatus === 'STOPPED'
                    ? "Click to start the scheduler (Currently STOPPED)"
                    : `Scheduler status: ${schedulerStatus || 'UNKNOWN'}`
            }>
              <Button
                variant={schedulerStatus === 'RUNNING' ? "contained" : "outlined"}
                size="small"
                onClick={schedulerStatus === 'RUNNING' ? handleStopScheduler : handleStartScheduler}
                startIcon={schedulerStatus === 'RUNNING' ? <StopIcon /> : <StartIcon />}
                color={schedulerStatus === 'RUNNING' ? "error" : "success"}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : schedulerStatus === 'RUNNING' ? "Stop Scheduler" : "Start Scheduler"}
              </Button>
            </Tooltip>

            <Tooltip title="View service details">
              <Button
                variant="outlined"
                size="small"
                onClick={handleShowDetails}
                startIcon={<DetailsIcon />}
                color="info"
                disabled={isLoading}
              >
                Details
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* All Metrics Grid - 4 cards per row */}
      <Grid container spacing={2}>
        {/* Row 1 */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Running Tasks"
            value={metrics?.runningTasks}
            loading={loading}
            icon="running"
            description="Currently executing"
            onRefresh={handleRefresh}
            lastUpdated={metrics?.lastUpdated}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Scheduled Tasks"
            value={metrics?.scheduledTasks}
            loading={loading}
            icon="scheduled"
            description="Waiting to execute"
            onRefresh={handleRefresh}
            lastUpdated={metrics?.lastUpdated}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Completed Tasks"
            value={metrics?.completedTasks}
            loading={loading}
            icon="completed"
            description="Successfully finished (24h)"
            onRefresh={handleRefresh}
            lastUpdated={metrics?.lastUpdated}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Failed Tasks (24h)"
            value={metrics?.failedTasksLast24h}
            loading={loading}
            icon="failed"
            description="Failed in past 24h"
            onRefresh={handleRefresh}
            lastUpdated={metrics?.lastUpdated}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Max Concurrent (24h)"
            value={metrics?.maxConcurrentTasksLast24h}
            loading={loading}
            icon="concurrent"
            description="Peak simultaneous"
            onRefresh={handleRefresh}
            lastUpdated={metrics?.lastUpdated}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Longest Running Task"
            value={performanceMetrics.longestRunningTaskDuration}
            loading={loading}
            icon="bottleneck"
            description="Current bottleneck detection"
            onRefresh={handleRefresh}
            lastUpdated={lastRefresh}
            color={performanceMetrics.longestRunningTaskDuration > 300 ? 'error' : 'primary'}
            valueComponent={
              performanceMetrics.longestRunningTaskDuration > 0 ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: performanceMetrics.longestRunningTaskDuration > 300 ? 'error.main' : 'primary.main',
                      lineHeight: 1,
                      display: 'inline'
                    }}
                  >
                    {Math.floor(performanceMetrics.longestRunningTaskDuration / 60)}m
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: performanceMetrics.longestRunningTaskDuration > 300 ? 'error.main' : 'primary.main',
                      lineHeight: 1,
                      display: 'inline',
                      ml: 0.5
                    }}
                  >
                    {performanceMetrics.longestRunningTaskDuration % 60}s
                  </Typography>
                </Box>
              ) : (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 500,
                    color: 'text.secondary',
                    textAlign: 'center',
                    lineHeight: 1
                  }}
                >
                  None
                </Typography>
              )
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Avg Execution Time"
            value={performanceMetrics.averageExecutionTimeFormatted}
            loading={loading}
            icon="performance"
            description="Last 24 hours average"
            onRefresh={handleRefresh}
            lastUpdated={lastRefresh}
            color="info"
            valueComponent={
              <Typography 
                variant={performanceMetrics.averageExecutionTimeFormatted === 'No data' ? 'h6' : 'h4'}
                sx={{ 
                  fontWeight: performanceMetrics.averageExecutionTimeFormatted === 'No data' ? 500 : 700,
                  color: performanceMetrics.averageExecutionTimeFormatted === 'No data' ? 'text.secondary' : 'info.main',
                  textAlign: 'center',
                  lineHeight: 1
                }}
              >
                {performanceMetrics.averageExecutionTimeFormatted}
              </Typography>
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricsWidget
            title="Next Scheduled Runs"
            value={performanceMetrics.nextScheduledCount}
            loading={loading}
            icon="schedule"
            description="Upcoming tasks"
            onRefresh={handleRefresh}
            lastUpdated={lastRefresh}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Hub Flow activity (Python Hub → sync_run_status + datahub_api_log) */}
      <HubFlowSummaryPanel />

      {/* Sync Run Status Grid */}
      <Box sx={{ mt: 4 }}>
        <SyncRunStatusGrid />
      </Box>

      {/* Additional Info */}
      {metrics && (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 2, 
            mt: 3, 
            borderRadius: 2,
            backgroundColor: 'rgba(0,0,0,0.02)',
            border: '1px solid rgba(0,0,0,0.08)'
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            💡 Metrics are automatically refreshed every 30 seconds when auto-refresh is enabled. 
            Click the refresh button to update immediately.
          </Typography>
        </Paper>
      )}

      {/* Service Details Modal */}
      <ServiceDetailsModal 
        open={showDetails}
        onClose={() => setShowDetails(false)}
        serviceDetails={serviceDetails}
      />

      <PostgresConnectionsModal
        open={showPostgresConnections}
        onClose={handleClosePostgresConnections}
        loading={postgresConnectionsLoading}
        error={postgresConnectionsError}
        data={postgresConnectionsData}
        scope={postgresConnectionsScope}
        onScopeChange={handlePostgresScopeChange}
        onRefresh={() => fetchPostgresConnections(postgresConnectionsScope)}
      />
    </Box>
  );
};

export default MetricsDashboard;